import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, CheckCheck, Landmark, Link as LinkIcon, Loader2, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import { creditSense } from "@/lib/api";
import { CreditSenseList } from "./CreditSenseList";
import { CreditSenseDetail } from "./CreditSenseDetail";
import { CreditSenseSetup, QuickLinkDialog } from "./CreditSenseSetup";
import { STATUS_LABEL, fmtTs } from "./creditSenseShared";

const LIMIT = 50;
const EMPTY = { q: "", status: "", linked: "", attention: "" };

function StatChip({ label, value, tone, active, onClick, testId, pulse }) {
  return (
    <button type="button" onClick={onClick} className={`relative min-w-0 rounded-lg border px-3 py-2 text-left transition ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`} data-testid={testId}>
      <div className={`truncate text-[10px] uppercase tracking-wide ${active ? "text-slate-300" : "text-slate-500"}`}>{label}</div>
      <div className={`font-mono text-lg font-bold tabular-nums ${active ? "text-white" : tone || "text-slate-900"}`}>{value ?? 0}</div>
      {pulse > 0 && !active && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500" />}
    </button>
  );
}

export default function CreditSenseHub({ onAttentionChange }) {
  const [filters, setFilters] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [attachFirst, setAttachFirst] = useState(false);
  const [quick, setQuick] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setFilters((f) => ({ ...f, q })); setOffset(0); }, 350); return () => clearTimeout(t); }, [q]);

  const loadAttention = useCallback(async () => {
    try { const r = await creditSense.attention(); onAttentionChange?.(r.count); } catch { /* badge is best-effort */ }
  }, [onAttentionChange]);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await creditSense.list({ ...filters, limit: LIMIT, offset })); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
    loadAttention();
  }, [filters, offset, loadAttention]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await creditSense.sync(7);
      if (r.ok === false) toast.warning(r.reason === "not configured" ? "Add the Credit Sense API key, API_Token and Store_Code under Settings → API Credentials first" : r.reason);
      else toast.success(`Checked Credit Sense: ${r.found} completed in the last 7 days${r.new ? ` · ${r.new} new` : " · nothing new"}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 9000 }); }
    finally { setSyncing(false); }
  };
  const markAll = async () => {
    try { const r = await creditSense.markSeen({ all: true }); toast.success(`${r.cleared} marked as seen`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v === "any" ? "" : v })); setOffset(0); };
  const toggle = (k, v) => set(k, filters[k] === v ? "" : v);
  const facets = data?.facets || {};
  const active = Object.entries(filters).filter(([k, v]) => v && k !== "q").length;
  const configured = data?.configured ?? true;

  return (
    <div className="space-y-3" data-testid="creditsense-hub">
      <div className="flex flex-wrap items-center gap-2">
        <Landmark className="h-5 w-5 text-emerald-600" />
        <h2 className="text-base font-semibold text-slate-900">Credit Sense · Australia bank statements</h2>
        <span className="text-xs text-slate-500" data-testid="creditsense-scope">{facets.total ?? "…"} reports{data?.last_sync ? ` · checked ${fmtTs(data.last_sync)}` : ""}</span>
        {!configured && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-900" data-testid="creditsense-notconfigured-pill">not connected yet — see Setup</span>}
        {facets.unseen > 0 && (
          <button type="button" onClick={() => toggle("attention", "yes")} className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-rose-700" data-testid="creditsense-attention-pill">
            <BellRing className="h-3 w-3" />{facets.unseen} new
          </button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {facets.unseen > 0 && <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-600" onClick={markAll} data-testid="creditsense-mark-all-seen"><CheckCheck className="mr-1 h-3.5 w-3.5" />Mark all seen</Button>}
          <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-600" onClick={() => setQuick(true)} data-testid="creditsense-quicklink-btn"><LinkIcon className="mr-1 h-3.5 w-3.5" />Send link</Button>
          <CreditSenseSetup />
          <Button size="sm" variant="outline" className="h-8" onClick={syncNow} disabled={syncing} data-testid="creditsense-sync-btn">
            {syncing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}<span className="hidden sm:inline">Check Credit Sense</span><span className="sm:hidden">Check</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6" data-testid="creditsense-stat-strip">
        <StatChip label="All" value={facets.total} active={!filters.status && !filters.attention && !filters.linked} onClick={() => { setFilters((f) => ({ ...f, status: "", attention: "", linked: "" })); setOffset(0); }} testId="creditsense-stat-all" />
        <StatChip label="New (unseen)" value={facets.unseen} tone="text-rose-700" pulse={facets.unseen} active={filters.attention === "yes"} onClick={() => toggle("attention", "yes")} testId="creditsense-stat-attention" />
        <StatChip label="Complete" value={facets.complete} tone="text-emerald-700" active={filters.status === "complete"} onClick={() => toggle("status", "complete")} testId="creditsense-stat-complete" />
        <StatChip label="In progress" value={facets.in_progress} tone="text-sky-700" active={filters.status === "in_progress"} onClick={() => toggle("status", "in_progress")} testId="creditsense-stat-progress" />
        <StatChip label="Errors" value={facets.error} tone="text-red-700" active={filters.status === "error"} onClick={() => toggle("status", "error")} testId="creditsense-stat-error" />
        <StatChip label="Attached to Monday" value={facets.linked} tone="text-slate-700" active={filters.linked === "yes"} onClick={() => toggle("linked", "yes")} testId="creditsense-stat-linked" />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm" data-testid="creditsense-filters">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, business, bank, reference, SFA number" className="h-8 pl-8 text-xs" data-testid="creditsense-search" />
        </div>
        <Select value={filters.status || "any"} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="creditsense-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any status</SelectItem>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.linked || "any"} onValueChange={(v) => set("linked", v)}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="creditsense-filter-linked"><SelectValue placeholder="Application" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Attached or not</SelectItem><SelectItem value="yes">Attached to an application</SelectItem><SelectItem value="no">Not attached yet</SelectItem></SelectContent>
        </Select>
        {(active > 0 || q) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => { setFilters(EMPTY); setQ(""); setOffset(0); }} data-testid="creditsense-filters-clear"><X className="mr-1 h-3.5 w-3.5" />Clear</Button>
        )}
        {loading && <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-400" data-testid="creditsense-loading" />}
      </div>

      <CreditSenseList data={data} loading={loading} offset={offset} limit={LIMIT} onPage={setOffset} configured={configured} onOpen={(id) => { setAttachFirst(false); setOpenId(id); }} onAttach={(id) => { setAttachFirst(true); setOpenId(id); }} />
      <CreditSenseDetail appId={openId} attachFirst={attachFirst} onClose={() => setOpenId(null)} onChanged={load} />
      {quick && <QuickLinkDialog onClose={() => setQuick(false)} />}
    </div>
  );
}
